// The copy guard on the paper room: refuses copy, cut, right-click, drag-out
// and the print/save keys on any page whose <body> carries
// `data-protected-content`, and hands the copier the notice instead.
//
// NOT IN packs/, for the reason mock_composer.js records; imported by
// packs/application.js beside question_elaboration.js, whose shape this is.
//
// The attribute is set by layouts/mock_room — the report, the review
// workbench, the objective lesson, the course report and the sitting itself —
// and its VALUE is ContentProtection::NOTICE. This file never types the
// sentence; it reads it from the body, so there is one place it is said.
//
// Four rules, each easy to undo by accident:
//
// - FORM FIELDS ARE EXEMPT BY KIND, not by attribute. The tutor chat box
//   (TutorChat, StudyChat), the exam room's [data-mock-answer] textarea and a
//   first-try written answer all keep select, copy and cut of the student's
//   OWN words with nothing to remember on the next input anyone adds.
// - PASTE IS NEVER TOUCHED. A student pasting their notes into the tutor
//   chat is doing exactly what the chat is for. Refusing paste mid-exam is
//   useMockIntegrity's job, and it keeps it.
// - The clipboard is written INSIDE the copy handler. setData is honoured
//   there and nowhere else, and a refusal that leaves the clipboard empty
//   looks like a bug rather than a rule — the notice is what tells them why.
// - No devtools-key interception (F12, Ctrl+Shift+I). The menu still opens
//   them, so the block protects nothing and only annoys.
//
// In the exam room this overlaps useMockIntegrity, and that is fine: both
// preventDefault the same events, the hook still reports its telemetry, and
// this adds the notice to the clipboard. This module reports nothing —
// Mocks::IntegrityController refuses a finished attempt, and the report and
// lesson pages have no event lane. It cannot stop a screenshot, view-source
// or the devtools; ContentProtection says so and this file does not pretend.

const TOAST_MS = 2500;
const REFUSED_KEYS = new Set(["p", "s", "u"]);

let toast = null;
let toastTimer = null;

function inField(event) {
  const target = event.target;
  return !!(target && target.closest && target.closest('input, textarea, select, [contenteditable="true"]'));
}

function showToast(notice) {
  if (toast === null) {
    toast = document.createElement("div");
    toast.className = "protected-content__toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.textContent = notice;
    document.body.appendChild(toast);
  }
  toast.classList.add("is-shown");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove("is-shown"), TOAST_MS);
}

function setup() {
  const notice = document.body.dataset.protectedContent;
  if (!notice) return;

  const refuse = (event) => {
    if (inField(event)) return;
    event.preventDefault();
    showToast(notice);
  };

  document.addEventListener("copy", (event) => {
    if (inField(event)) return;
    event.preventDefault();
    if (event.clipboardData) event.clipboardData.setData("text/plain", notice);
    showToast(notice);
  });
  document.addEventListener("cut", refuse);
  // A right-click on an exam room's own tool (striking an option out) is
  // still refused its menu, but it is the tool working, so no notice.
  document.addEventListener("contextmenu", (event) => {
    if (event.target && event.target.closest && event.target.closest("[data-exam-tool]")) {
      event.preventDefault();
      return;
    }
    refuse(event);
  });
  document.addEventListener("dragstart", (event) => {
    if (inField(event)) return;
    event.preventDefault();
  });
  // Print, save page, view source. Ctrl+C rides the copy event above, so it
  // is not here; blocking the key would also refuse it inside a field.
  document.addEventListener("keydown", (event) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    if (!REFUSED_KEYS.has(event.key.toLowerCase())) return;
    event.preventDefault();
    showToast(notice);
  });
}

// The same guard every module in this pack carries — see mock_request_wizard.js.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
